import type { Category, Page, Product, ProductQuery } from '@/types';
import { reviewsFor } from '@/data/catalog';
import { DEMO_TENANT_ID } from '@/data/adminSeed';
import { ServiceError, type CatalogService } from '../contracts';
import { delay, shouldFail } from './db';
import { dataFor } from './admin/context';

/** The storefront is a READ view over the tenant's admin data: only ACTIVE products in ACTIVE categories are public. */
const tenant = () => dataFor(DEMO_TENANT_ID);
const productsSeed = (): Product[] => {
  const d = tenant();
  const visible = new Set(d.categories.filter((c) => c.status === 'ACTIVE').map((c) => c.id));
  return d.products.filter((p) => p.status === 'ACTIVE' && visible.has(p.categoryId));
};
const categories = (): Category[] => {
  const ps = productsSeed();
  return tenant().categories.filter((c) => c.status === 'ACTIVE').sort((a, b) => a.sortOrder - b.sortOrder)
    .map((c) => ({ id: c.id, slug: c.slug, name: c.name, description: c.description, icon: c.icon, hue: c.hue, productCount: ps.filter((p) => p.categoryId === c.id).length }));
};

const guard = () => {
  if (shouldFail()) throw new ServiceError('Simulated service failure (?fail is set).');
};

function runQuery(q: ProductQuery = {}): Page<Product> {
  const cats = categories();
  let items = productsSeed().slice();

  if (q.categorySlug) {
    const c = cats.find((x) => x.slug === q.categorySlug);
    items = c ? items.filter((p) => p.categoryId === c.id) : [];
  }
  if (q.q?.trim()) {
    const terms = q.q.toLowerCase().split(/\s+/).filter(Boolean);
    items = items.filter((p) => {
      const hay = `${p.name} ${p.brand} ${p.tags.join(' ')} ${cats.find((c) => c.id === p.categoryId)?.name ?? ''}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }
  if (q.minPrice != null) items = items.filter((p) => p.price >= q.minPrice!);
  if (q.maxPrice != null) items = items.filter((p) => p.price <= q.maxPrice!);
  if (q.minRating) items = items.filter((p) => p.rating >= q.minRating!);
  if (q.inStock) items = items.filter((p) => p.stock > 0);
  if (q.onSale) items = items.filter((p) => p.compareAtPrice && p.compareAtPrice > p.price);

  switch (q.sort) {
    case 'price-asc': items.sort((a, b) => a.price - b.price); break;
    case 'price-desc': items.sort((a, b) => b.price - a.price); break;
    case 'rating': items.sort((a, b) => b.rating - a.rating || b.reviewCount - a.reviewCount); break;
    case 'newest': items.sort((a, b) => b.createdAt.localeCompare(a.createdAt)); break;
    default: items.sort((a, b) => Number(b.featured) - Number(a.featured) || b.reviewCount - a.reviewCount);
  }

  const pageSize = q.pageSize ?? 12;
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, q.page ?? 1), totalPages);
  return { items: items.slice((page - 1) * pageSize, page * pageSize), total, page, pageSize, totalPages };
}

export const mockCatalog: CatalogService = {
  async listCategories() {
    guard();
    return delay(categories());
  },
  async getCategory(slug) {
    guard();
    const c = categories().find((x) => x.slug === slug);
    if (!c) throw new ServiceError('Category not found', 'NOT_FOUND');
    return delay(c);
  },
  async listProducts(query) {
    guard();
    return delay(runQuery(query));
  },
  async getProduct(slug) {
    guard();
    const p = productsSeed().find((x) => x.slug === slug);
    if (!p) throw new ServiceError('Product not found', 'NOT_FOUND');
    return delay(p);
  },
  async getProductsByIds(ids) {
    return delay(productsSeed().filter((p) => ids.includes(p.id)), 120);
  },
  async getRelated(productId, limit = 4) {
    const all = productsSeed();
    const p = all.find((x) => x.id === productId);
    const rel = all.filter((x) => x.id !== productId && x.categoryId === p?.categoryId).slice(0, limit);
    const fill = all.filter((x) => x.id !== productId && !rel.includes(x)).slice(0, limit - rel.length);
    return delay([...rel, ...fill]);
  },
  async listReviews(productId) {
    return delay(reviewsFor(productId));
  },
  async suggest(q) {
    const t = q.trim().toLowerCase();
    return delay(t.length < 2 ? [] : productsSeed().filter((p) => p.name.toLowerCase().includes(t)).slice(0, 5), 120);
  },
};
